import { DEFAULT_BILLING_TYPE } from '@/utils/defaults';
import { default as CONSTANTS } from '@/config/constants';
import i18n from './i18n';

const dayjs = require("dayjs");
const utc = require('dayjs/plugin/utc');
const customParseFormat = require('dayjs/plugin/customParseFormat');

dayjs.extend(utc);
dayjs.extend(customParseFormat);

export function formatDateTime(datetime, format = "HH:mm DD/MM/YYYY") {
  if (!datetime) return '';
  if (datetime?.includes('Z')) {
    return dayjs(datetime).format(format);
  }
  return dayjs(datetime + 'Z').format(format);
}

export function formatDateTime2(datetime, format = "HH:mm DD/MM/YYYY") {
  if (!datetime) return '';
  return dayjs(datetime).format(format);
}

export function formatDateTime3(datetime, format = "HH:mm DD/MM/YYYY") {
  if (!datetime) return '';
  return dayjs.utc(datetime, "YYYYMMDDTHHmmss[Z]").format(format);
}

export function formatDateTimeCheckTZ(time, format = 'HH:mm DD/MM/YYYY') {
  if (time) {
    if (time.endsWith('Z') || time.includes('+')) {
      return dayjs(time).format(format);
    }
    return dayjs(time + "Z").format(format);
  }
  return null
}

export function formatDateTimeValid(datetime, format = "HH:mm DD/MM/YYYY") {
  let dayObject = null;
  if (datetime?.includes('Z')) {
    dayObject = dayjs(datetime);
  }
  else {
    dayObject = dayjs(datetime + 'Z');
  }
  if (dayObject.isValid()) {
    return dayObject.format(format);
  }
  return ""
}

// Backend datetime strings arrive without a timezone offset but are always UTC - a bare
// `new Date(str)`/`dayjs(str)` would misinterpret them as local time, so force `Z` when missing.
export function parseServerDateTime(dateTimeStr) {
  if (!dateTimeStr) return null;
  const hasTz = dateTimeStr.includes('Z') || dateTimeStr.includes('+');
  return dayjs(hasTz ? dateTimeStr : `${dateTimeStr}Z`);
}

export function daysRemaining(expireAt) {
  const end = parseServerDateTime(expireAt);
  if (!end) return 0;
  return Math.max(0, Math.ceil(end.diff(dayjs(), 'day', true)));
}

export function numberWithDelimiter(number, delimiter = '.') {
  return Math.round(number)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, delimiter)
}

export function moneyFormat(number, currency = 'đ') {
  if (number) {
    return numberWithDelimiter(number) + currency
  }
  return `0${currency}`
}

export function parseVolumeStatus(status) {
  switch (status) {
    case 'available':
      return i18n.t('filters.volumeStatus.available')
    case 'in-use':
      return i18n.t('filters.volumeStatus.inUse')
    case 'error':
      return i18n.t('constants.invoiceStatus.error')
    case 'extending':
      return i18n.t('filters.volumeStatus.extending')
    case 'processing':
      return i18n.t('common.inProgress')
    case 'success':
      return i18n.t('common.inProgress')
    case 'retyping':
      return i18n.t('filters.volumeStatus.retyping')
    default:
      return status.toUpperCase()
  }
}

export function parseTicketStatus(status) {
  switch (status) {
    case 'open':
      return i18n.t('filters.ticketStatus.open')
    case 'closed':
      return i18n.t('filters.ticketStatus.closed')
    default:
      return i18n.t('filters.ticketStatus.open')
  }
}

export function parseFlavorFormat(flavor) {
  const num = flavor.match(/\d+/g)?.map(Number);
  return i18n.t('filters.flavorFormat', { cpu: num?.[0], ram: num?.[1] })
}

export function capitalizeWords(text) {
  return text?.replace(/\b\w/g, char => char.toUpperCase());
}

export function getSubnet(id, subnets) {
  return subnets.find((s) => s.id === id)?.name;
}

export function getServerGroup(id, groups) {
  return groups.find((s) => s.id === id)?.name;
}

export function byteToSize(bytes) {
  const thresh = 1024;
  if (!bytes) {
    return '0 B';
  }
  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`;
  }
  const units = ['KB', 'MB', 'GB', 'TB'];
  let u = -1;
  let capacity = bytes;
  do {
    capacity /= thresh;
    u += 1;
  } while (Math.abs(capacity) >= thresh && u < units.length - 1);

  return `${parseFloat(capacity.toFixed(2))} ${units[u]}`.toString();
}

export function MappingPaymentMethod(label) {
  switch (label) {
    case 'manual':
      return i18n.t('constants.paymentMethod.manual')
    case 'refund':
      return i18n.t('constants.paymentMethod.refund')
    case 'baokim':
      return "Bảo Kim"

    default:
      return label
  }
}

export function getResource(item) {
  if (!item) return { info: '', product: '' };
  const { BILLING_PLAN } = CONSTANTS;
  const unit = item.types?.find((type) => type?.includes('unit'))?.split(':')?.[1];
  let product = item.types?.find((type) => type?.includes('product'))?.split(':')?.[1];
  const { product_display_name } = item;
  const noShowQuantitePrefixs = ['cs_flavor'];
  const baseProductName = product_display_name || showProductName(product) || '';
  const planType = item.types?.find((type) => BILLING_PLAN?.includes(type)) ?? DEFAULT_BILLING_TYPE;
  const planTypeString = planType ? `${i18n.t(`public.${planType}`)}` : '';
  const quantity = noShowQuantitePrefixs.some(
    (prefix) => product?.startsWith(prefix)
  ) ? '' : `${item?.raw_quantity?.quantity ?? ''} ${unit ?? ''}`;
  if (item.types?.includes('refund')) {
    return {
      info: `${i18n.t('public.refund')} ${planTypeString.toLowerCase()}: ${item?.resource_name ?? ''}`,
      product: `${baseProductName} ${quantity}`,
    };
  }
  if (item.types?.includes('restore')) {
    // Crediting back the pre-scale baseline quantity so only the delta gets charged - not a
    // refund (nothing was ever charged for it to begin with), so it gets its own label.
    return {
      info: `${i18n.t('public.restore')} ${planTypeString.toLowerCase()}: ${item?.resource_name ?? ''}`,
      product: `${baseProductName} ${quantity}`,
    };
  }
  return {
    info: `${planTypeString}: ${item.resource_name ?? ''}`,
    product: `${baseProductName} ${quantity}`,
  }
}

export function showProductName(productName) {
  if (!productName) return '';
  if (productName.startsWith('cs_flavor')) {
    const parts = productName.split('_').slice(-2);
    if (parts.length === 2) {
      const cpuMatch = parts[0].match(/\d+/);
      const ramMatch = parts[1].match(/\d+/);
      if (cpuMatch && ramMatch) {
        return `${cpuMatch[0]} CPU, ${ramMatch[0]} GB RAM`;
      }
    }
    return productName;
  } else {
    return productName;
  }
}

// Excel/Sheets treat a leading =, +, -, or @ as a formula, so an untrusted cell value
// starting with one of these could execute arbitrary commands when the CSV is opened.
// Prefixing with a single quote forces spreadsheet apps to treat it as plain text.
function sanitizeCsvCell(cell) {
  const value = cell === null || cell === undefined ? '' : String(cell);
  return /^[=+\-@]/.test(value) ? `'${value}` : value;
}

export function exportCSV(rows, fileName) {
  var universalBOM = "\uFEFF";
  const csvContent = "data:text/csv;charset=utf-8," + universalBOM + rows?.map(e => e.map(sanitizeCsvCell).join(",")).join("\n");
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", `${fileName}.csv`);
  document.body.appendChild(link);

  link.click();
}

/**
 * Displays a human-readable summary for an action record.
 *
 * Handles two contexts:
 * 1. Server Action History records: `record.action` is a String (e.g. "power_on", "os-start").
 *    - Looks up ACTIVITY_LOG_SERVER_ACTIONS for a friendly label, then falls back to i18n or raw value.
 * 2. General Activity Log records: `record.action` is an Object (from DB join).
 *    - Uses action_summary/action_name, then appends the specific action payload for patch/post routes.
 *    - Format: "Thực hiện hành động - Bật" for server actions.
 */
export function displayActionSummary(record) {
  if (!record) return '-';

  const actionPayload = record.request?.action;
  const method = record.method?.toLowerCase();
  const routePath = record.route_path;

  // Context 1: Server History — record.action is a plain string
  if (typeof record.action === 'string') {
    // Check ACTIVITY_LOG_SERVER_ACTIONS first for a friendly label
    const serverActionLabel = CONSTANTS.ACTIVITY_LOG_SERVER_ACTIONS?.[record.action];
    if (serverActionLabel) return serverActionLabel;

    // Fall back to i18n translation
    if (i18n.te(`server.${record.action}`)) {
      return i18n.t(`server.${record.action}`);
    }

    // If action is a known server-action route but the string is generic (e.g. "action_server"),
    // try to resolve via request body payload
    if (method === 'patch' && routePath?.includes('/actions') && actionPayload) {
      return CONSTANTS.ACTIVITY_LOG_SERVER_ACTIONS?.[actionPayload] || actionPayload;
    }

    return record.action;
  }

  // Context 2: General Activity Log — record.action is an Object
  let summary = record.action_summary || record.action_name;

  if (!summary) {
    if (record.action && typeof record.action === 'object') {
      summary = record.action.summary || record.action.name || '-';
    } else {
      summary = '-';
    }
  }

  // Append specific action label for patch /actions routes (server actions)
  if (method === 'patch' && routePath?.includes('/actions') && actionPayload) {
    const actionName = CONSTANTS.ACTIVITY_LOG_SERVER_ACTIONS?.[actionPayload] || actionPayload;
    if (typeof summary === 'string' && !summary.includes(actionName)) {
      if (summary === 'action_server') {
        // Raw DB action key: just show the friendly action name
        summary = actionName;
      } else {
        // Human-readable summary: use short prefix + action name
        summary = i18n.t('filters.performAction', { action: actionName });
      }
    }
  } else if (method === 'post' && routePath?.includes('/orders') && actionPayload) {
    // Append order action label
    const actionName = CONSTANTS.ACTIVITY_LOG_ORDER_ACTIONS?.[actionPayload] || actionPayload;
    if (typeof summary === 'string' && !summary.includes(actionName)) {
      summary += ` - ${actionName}`;
    }
  }

  return summary;
}
